import { h, formatDate, formatTime, formatDuration, formatWeekday } from '../ui/dom.js';
import { card, button, emptyState, metric, readinessBadge, badge, toast } from '../ui/components.js';
import { openPrintWindow } from '../ui/print.js';
import { api, ApiError } from '../api.js';
import { can } from '../store.js';
import type { Page, PageContext } from '../page.js';
import { READINESS_LABELS, type AgendaDay, type AgendaItem, type AgendaView } from '../types.js';

export async function agendaPage(context: PageContext): Promise<Page> {
  const { id } = context.params;
  if (!id) return { title: 'Agenda', body: emptyState('Edição não informada.') };

  if (can.isMinistrador()) {
    context.navigate('/minhas-ministracoes');
    return { title: 'Redirecionando…', body: null };
  }

  const view = await api.get<AgendaView>(`/api/editions/${id}/agenda`);
  const { edition, items, totals } = view;

  const recalc = async (): Promise<void> => {
    try {
      await api.post(`/api/editions/${id}/schedule/recompute`);
      toast.success('Cronograma recalculado');
      window.location.reload();
    } catch (error) {
      toast.error('Falha ao recalcular', error instanceof ApiError ? error.message : undefined);
    }
  };

  /* ---------- Tabelas por dia ------------------------------------------ */

  const buildDayTable = (day: AgendaDay): HTMLElement => {
    const rows: HTMLElement[] = [];

    for (const item of day.items) {
      rows.push(
        h(
          'tr',
          null,
          h(
            'td',
            { class: 'agenda-table__time' },
            item.scheduledStart && item.scheduledEnd
              ? `${formatTime(item.scheduledStart)} — ${formatTime(item.scheduledEnd)}`
              : '—',
          ),
          h(
            'td',
            null,
            h('div', { style: { fontWeight: '600' }, text: item.title }),
            item.theme ? h('div', { class: 'muted small', text: item.theme }) : null,
          ),
          h('td', null, item.ministradorName ?? h('span', { class: 'faint', text: 'a definir' })),
          h('td', { class: 'agenda-table__duration', text: formatDuration(item.durationMin) }),
          h(
            'td',
            null,
            item.intervalMin > 0
              ? h('span', {
                  class: 'agenda-table__duration',
                  text: `+${formatDuration(item.intervalMin)}`,
                })
              : h('span', { class: 'faint', text: '—' }),
          ),
          h('td', null, readinessBadge(item.readiness)),
        ),
      );

      if (item.intervalMin > 0) {
        rows.push(
          h(
            'tr',
            { class: 'agenda-row--break' },
            h(
              'td',
              { class: 'agenda-table__time' },
              item.scheduledEnd ? formatTime(item.scheduledEnd) : '—',
            ),
            h(
              'td',
              { attrs: { colspan: '5' } },
              h('span', {
                class: 'agenda-row__break-label',
                text: `Intervalo de ${formatDuration(item.intervalMin)}${
                  item.nextStartsAt ? ` · retorno às ${formatTime(item.nextStartsAt)}` : ''
                }`,
              }),
            ),
          ),
        );
      }
    }

    return h(
      'table',
      { class: 'agenda-table' },
      h(
        'thead',
        null,
        h(
          'tr',
          null,
          h('th', { text: 'Horário' }),
          h('th', { text: 'Ministração' }),
          h('th', { text: 'Ministrador' }),
          h('th', { text: 'Duração' }),
          h('th', { text: 'Intervalo' }),
          h('th', { text: 'Status' }),
        ),
      ),
      h('tbody', null, ...rows),
    );
  };

  // Encontros de vários dias: uma seção por dia, com horário de início próprio.
  const daySections: HTMLElement[] = view.days.map((day, index) => {
    const dayDate = formatDate(day.startAt);

    return h(
      'div',
      {
        class: 'stack',
        style: {
          marginTop: index === 0 ? '0' : '32px',
          paddingTop: index === 0 ? '0' : '24px',
          borderTop: index === 0 ? 'none' : '1px dashed var(--border-strong)',
        },
      },
      h(
        'div',
        { class: 'row row--between' },
        h(
          'div',
          { class: 'row' },
          badge(`Dia ${day.dayNumber}`, 'accent'),
          h(
            'div',
            null,
            h('div', { style: { fontWeight: '700', fontSize: '15px' }, text: day.label ?? `Dia ${day.dayNumber}` }),
            h('div', {
              class: 'muted small',
              text: `${formatWeekday(day.startAt)}, ${dayDate} · início ${formatTime(day.startAt)}${
                day.endsAt ? ` · término ${formatTime(day.endsAt)}` : ''
              }`,
            }),
          ),
        ),
        h(
          'div',
          { class: 'row small muted' },
          h('span', { text: `${day.totals.ministrations} ministração(ões)` }),
          h('span', { text: '·' }),
          h('span', { text: formatDuration(day.totals.totalMinutes) }),
        ),
      ),
      day.items.length > 0
        ? buildDayTable(day)
        : emptyState('Nenhuma ministração neste dia.', '▤'),
      h(
        'div',
        { class: 'agenda-totals' },
        h('span', { text: `Fala: ${formatDuration(day.totals.speakingMinutes)}` }),
        h('span', { text: `Intervalos: ${formatDuration(day.totals.intervalMinutes)}` }),
        h('span', { text: `Total do dia: ${formatDuration(day.totals.totalMinutes)}` }),
        h('span', { text: `Homologadas: ${day.totals.homologadas}/${day.totals.ministrations}` }),
      ),
    );
  });

  /* ---------- Exportação ------------------------------------------------- */

  const exportPdf = (): void => {
    openPrintWindow(
      `Cronograma — ${edition.name}`,
      buildAgendaPrintHtml(view),
      `${edition.location ?? 'local não informado'} · ${totals.days} dia(s) · ${totals.ministrations} ministrações · ${formatDuration(totals.totalMinutes)}`,
    );
  };

  const exportText = (): void => {
    const lines: string[] = [
      `CRONOGRAMA — ${edition.name}`,
      `${edition.location ?? 'local não informado'} · ${view.totals.days} dia(s) · ${formatDuration(view.totals.totalMinutes)} no total`,
      '',
    ];

    for (const day of view.days) {
      lines.push('='.repeat(72));
      lines.push(
        `DIA ${day.dayNumber} — ${day.label ?? ''} · ${formatWeekday(day.startAt)}, ${formatDate(day.startAt)}`,
      );
      lines.push(
        `Início ${formatTime(day.startAt)}${day.endsAt ? ` · término ${formatTime(day.endsAt)}` : ''} · ${day.totals.ministrations} ministração(ões)`,
      );
      lines.push('='.repeat(72));

      for (const item of day.items) {
        const time =
          item.scheduledStart && item.scheduledEnd
            ? `${formatTime(item.scheduledStart)}-${formatTime(item.scheduledEnd)}`
            : '--:--';
        const responsible = item.ministradorName ?? 'a definir';
        lines.push(
          `${time}  ${item.title.padEnd(32, ' ')} ${formatDuration(item.durationMin).padStart(7)}  ${responsible}`,
        );
        if (item.intervalMin > 0) {
          lines.push(
            `        (intervalo de ${formatDuration(item.intervalMin)})${
              item.nextStartsAt ? ` · retorno às ${formatTime(item.nextStartsAt)}` : ''
            }`,
          );
        }
      }

      lines.push(
        '',
        `Total do dia: ${formatDuration(day.totals.totalMinutes)} (fala ${formatDuration(day.totals.speakingMinutes)} + intervalos ${formatDuration(day.totals.intervalMinutes)})`,
        '',
      );
    }

    lines.push(`TOTAL GERAL: ${formatDuration(view.totals.totalMinutes)} · ${view.totals.ministrations} ministrações
`);

    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = h('a', {
      href: url,
      download: `cronograma-${edition.name.replace(/\s+/g, '-').toLowerCase()}.txt`,
    });
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 20_000);
  };

  /* ---------- Montagem --------------------------------------------------- */

  const body = [
    h(
      'div',
      { class: 'grid agenda-actions' },
      h(
        'div',
        { class: totals.days > 1 ? 'grid grid--4' : 'grid grid--4' },
        metric('Dias de encontro', totals.days, 'accent'),
        metric('Ministrações', totals.ministrations, 'info'),
        metric('Fala / intervalos', `${formatDuration(totals.speakingMinutes)} / ${formatDuration(totals.intervalMinutes)}`, 'warning'),
        metric('Duração total', formatDuration(totals.totalMinutes), 'success'),
      ),
    ),
    card({
      title: 'Situação da agenda',
      hint:
        totals.days > 1
          ? 'Cronograma de vários dias, gerado automaticamente a partir do modelo e das escalas.'
          : 'Cronograma gerado automaticamente a partir do modelo e das escalas.',
      body: h(
        'div',
        { class: 'row row--between' },
        h(
          'div',
          { class: 'row' },
          badge(`${totals.days} dia(s)`, 'accent'),
          badge(`${totals.homologadas} homologada(s)`, 'success'),
          badge(`${totals.pendentes} pendente(s)`, totals.pendentes > 0 ? 'warning' : 'neutral'),
          view.startsAt
            ? badge(`Início ${formatDate(view.startsAt)} ${formatTime(view.startsAt)}`, 'info')
            : null,
        ),
        can.createEditions()
          ? button('Recalcular agenda', { variant: 'ghost', size: 'sm', onClick: () => void recalc() })
          : null,
      ),
    }),
    h(
      'div',
      { class: 'agenda-sheet' },
      h(
        'div',
        { class: 'agenda-head' },
        h('div', { class: 'agenda-head__title', text: edition.name }),
        h('div', {
          class: 'agenda-head__meta',
          text: `${edition.location ?? 'local não informado'} · ${totals.days} dia(s) de encontro`,
        }),
        h('div', {
          class: 'agenda-head__meta',
          text: `${totals.ministrations} ministrações · ${formatDuration(totals.speakingMinutes)} de fala · ${formatDuration(totals.intervalMinutes)} de intervalos · total ${formatDuration(totals.totalMinutes)}`,
        }),
      ),
      view.days.length > 0
        ? h('div', null, ...daySections)
        : emptyState('Nenhuma ministração nesta edição.', '▤'),
      h(
        'div',
        { class: 'agenda-legend' },
        h('span', { text: '• Horários calculados automaticamente pelo sistema' }),
        h('span', { text: '• Cada dia tem seu próprio horário de início' }),
        h('span', { text: '• Intervalos não contam como tempo de fala' }),
        h('span', { text: '• A ordem segue as ministrações definidas no modelo' }),
      ),
    ),
  ];

  return {
    title: 'Agenda / Cronograma',
    subtitle: `${edition.name} · gerado a partir do modelo mestre e das escalas`,
    actions: [
      button('Voltar à edição', {
        variant: 'ghost',
        size: 'sm',
        onClick: () => context.navigate(`/edicoes/${id}`),
      }),
      button('Check-list', {
        variant: 'ghost',
        size: 'sm',
        onClick: () => context.navigate(`/edicoes/${id}/prontidao`),
      }),
      button('Exportar TXT', { variant: 'ghost', size: 'sm', onClick: exportText }),
      button('Exportar PDF', { variant: 'subtle', size: 'sm', onClick: exportPdf }),
    ],
    body,
  };
}

/**
 * Monta o HTML imprimível do cronograma: uma seção por dia do encontro,
 * no formato A4 com a tipografia ABNT.
 */
function buildAgendaPrintHtml(view: AgendaView): string {
  const renderRows = (items: AgendaItem[]): string =>
    items
      .map((item: AgendaItem) => {
        const time =
          item.scheduledStart && item.scheduledEnd
            ? `${formatTime(item.scheduledStart)} — ${formatTime(item.scheduledEnd)}`
            : '—';
        const main = `
        <tr>
          <td>${time}</td>
          <td>${escapeHtml(item.title)}</td>
          <td>${escapeHtml(item.ministradorName ?? 'a definir')}</td>
          <td>${formatDuration(item.durationMin)}</td>
          <td>${item.intervalMin > 0 ? `+${formatDuration(item.intervalMin)}` : '—'}</td>
          <td>${READINESS_LABELS[item.readiness]}</td>
        </tr>`;

        if (item.intervalMin === 0) return main;

        const brk = `
        <tr>
          <td>${item.scheduledEnd ? formatTime(item.scheduledEnd) : '—'}</td>
          <td colspan="5"><em>Intervalo de ${formatDuration(item.intervalMin)}${
            item.nextStartsAt ? ` · retorno às ${formatTime(item.nextStartsAt)}` : ''
          }</em></td>
        </tr>`;

        return main + brk;
      })
      .join('');

  const daysHtml = view.days
    .map(
      (day) => `
    <h2 style="text-align:left;text-indent:0;margin-top:1.6em;">
      Dia ${day.dayNumber}${day.label ? ` — ${escapeHtml(day.label)}` : ''}
    </h2>
    <p style="text-align:left;text-indent:0;font-size:10pt;">
      ${formatWeekday(day.startAt)}, ${formatDate(day.startAt)} · início ${formatTime(day.startAt)}${
        day.endsAt ? ` · término ${formatTime(day.endsAt)}` : ''
      } · ${day.totals.ministrations} ministração(ões)
    </p>
    <table>
      <thead>
        <tr>
          <th>Horário</th>
          <th>Ministração</th>
          <th>Ministrador</th>
          <th>Duração</th>
          <th>Intervalo</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>${renderRows(day.items)}</tbody>
    </table>
    <p style="text-align:left;text-indent:0;font-size:10pt;">
      <strong>Total do dia:</strong> ${formatDuration(day.totals.totalMinutes)}
      (fala ${formatDuration(day.totals.speakingMinutes)} + intervalos ${formatDuration(day.totals.intervalMinutes)}) ·
      <strong>Homologadas:</strong> ${day.totals.homologadas}/${day.totals.ministrations}
    </p>
  `,
    )
    .join('<hr />');

  return `
    ${daysHtml}
    <h2 style="text-align:left;text-indent:0;">Resumo geral</h2>
    <p style="text-align:left;text-indent:0;">
      <strong>Dias:</strong> ${view.totals.days} ·
      <strong>Ministrações:</strong> ${view.totals.ministrations} ·
      <strong>Tempo de fala:</strong> ${formatDuration(view.totals.speakingMinutes)} ·
      <strong>Intervalos:</strong> ${formatDuration(view.totals.intervalMinutes)} ·
      <strong>Total:</strong> ${formatDuration(view.totals.totalMinutes)}
    </p>
    <p style="text-align:left;text-indent:0;">
      <strong>Homologadas:</strong> ${view.totals.homologadas} ·
      <strong>Pendentes:</strong> ${view.totals.pendentes}
    </p>
  `;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
